import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowRight, Inbox } from 'lucide-react';
import Button from '../ui/Button';
import { ToastRenderer } from '../../hooks/useToast';
import { getApiErrorMessage } from '../../utils/apiErrors';
import type { PendingActionGroup, PendingActionItem } from '../../api/pendingActions';
import PendingActionRow, { type RowDecision } from './PendingActionRow';
import DecisionDialog from './DecisionDialog';
import DetailDialog from './DetailDialog';
import { getSourceRoute } from './dispatch';
import { usePendingActionDecision, pendingActionKey } from './usePendingActionDecision';
import { notifyPendingActionsChanged } from './usePendingActions';
import { submitSurveyResponse } from '../../api/PreformanceManagement/Survey';
import { useComplianceCountry } from '../../hooks/useComplianceCountry';
import { useMySubscription } from '../../hooks/useMySubscription';

interface PendingActionsPanelProps {
  variant: 'dashboard' | 'full';
  items: PendingActionItem[];
  countsByGroup: Partial<Record<PendingActionGroup, number>>;
  totalCount: number;
  /** Silent refetch after an action resolves (or fails as already-resolved). */
  onChanged: () => Promise<void> | void;
  /** Dashboard variant: how many rows before "View all". */
  topN?: number;
}

const GROUP_ORDER: PendingActionGroup[] = ['ForYou', 'Approvals'];

/**
 * The shared Pending Actions inbox list: "For you" pinned above "Approvals",
 * inline quick actions on every row, details always one click away.
 * Owns the decision flow (confirm dialog → domain dispatch → toast → refetch);
 * hosts pass data in and refetch on change.
 */
const PendingActionsPanel: React.FC<PendingActionsPanelProps> = ({
  variant,
  items,
  countsByGroup,
  totalCount,
  onChanged,
  topN = 5,
}) => {
  const { t } = useTranslation('requests');
  const navigate = useNavigate();
  const locale = useSelector((state: { user?: { locale?: string } }) => state.user?.locale) || 'en';
  const role = useSelector((state: { user?: { role?: string } }) => state.user?.role) || '';
  const isManager = useSelector((state: { user?: { isManager?: boolean } }) => state.user?.isManager) || false;
  // Non-role route gates (see getSourceRoute). Effective shift-swap enablement is the
  // per-user override else the company default, exactly as ShiftSwapEnabledRoute resolves it —
  // resolving it differently here would deep-link to a page the guard bounces to '/'.
  const shiftSwapConfig = useSelector((state: { user?: { userRules?: { shiftSwapConfig?: string } } }) => state.user?.userRules?.shiftSwapConfig);
  const companyShiftSwapEnabled = useSelector((state: { user?: { company?: { shiftSwapEnabled?: boolean } } }) => state.user?.company?.shiftSwapEnabled);
  const shiftSwapEnabled =
    shiftSwapConfig === 'Enabled' ? true :
    shiftSwapConfig === 'Disabled' ? false :
    !!companyShiftSwapEnabled;
  const complianceCountry = useComplianceCountry();
  // Subscription gate. useMySubscription is a SHARED module-level store, so this adds no
  // extra /api/Subscription/me fetch. Left `undefined` until the tier has actually resolved
  // (the hook falls back to "Free" before the first response) — an unresolved tier must not
  // strip a Paid user's deep links for a frame. Operators are never soft-locked.
  const { isFree, isOperator, data: subscriptionData, isError: subscriptionError } = useMySubscription();
  const isFreeTier = subscriptionData !== null || subscriptionError ? isFree && !isOperator : undefined;
  const routeContext = { role, isManager, shiftSwapEnabled, complianceCountry, isFreeTier };
  const [detailItem, setDetailItem] = useState<PendingActionItem | null>(null);
  const [surveyBusy, setSurveyBusy] = useState<{ key: string; decision: RowDecision } | null>(null);

  const rowKey = pendingActionKey;

  const {
    decisionTarget,
    submitting,
    busyFor,
    requestDecision,
    confirmDecision,
    closeDecision,
    toast,
    showToast,
    dismissToast,
  } = usePendingActionDecision({ onChanged });

  const visibleItems = useMemo(() => {
    if (variant === 'full') return items;
    // Dashboard: a taste of the inbox — ForYou first (server already pins
    // blocking + sorts), capped at topN across both groups.
    return items.slice(0, topN);
  }, [items, variant, topN]);

  const grouped = useMemo(
    () =>
      GROUP_ORDER.map((group) => ({
        group,
        items: visibleItems.filter((i) => i.group === group),
      })).filter((g) => g.items.length > 0),
    [visibleItems],
  );

  // One deep-dive rule for both the OpenOnly CTA and Details: navigate to the
  // page where the request actually lives; the in-inbox dialog is only the
  // fallback for pageless sources (or roles the target route would bounce).
  const handleDeepDive = (item: PendingActionItem) => {
    const route = getSourceRoute(item, routeContext);
    if (route) navigate(route);
    else setDetailItem(item);
  };

  /** True when Details will navigate (drives the row's aria semantics). */
  const detailsNavigates = (item: PendingActionItem) =>
    getSourceRoute(item, routeContext) !== undefined;

  // The monthly pulse is one 0–10 question: answer it right on the row.
  const runQuickRate = async (item: PendingActionItem, rating: number) => {
    setSurveyBusy({ key: rowKey(item), decision: `rate-${rating}` });
    try {
      const result = await submitSurveyResponse({ rating });
      if (result.errors.length === 0) {
        showToast(t('Done, removed from your pending actions'), 'success');
      } else {
        // Already-resolved tolerance (e.g. answered on mobile): surface the
        // domain error, then refetch so the row disappears.
        showToast(getApiErrorMessage(result, t, t('Something went wrong')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Something went wrong')), 'error');
    }
    await onChanged();
    notifyPendingActionsChanged();
    setSurveyBusy(null);
  };

  const emptyState = (
    <div className="flex flex-col items-center gap-2 py-10 text-center" data-testid="pa-empty">
      <Inbox size={28} className="text-muted-foreground" aria-hidden="true" />
      <p className="text-sm font-medium text-foreground">{t("You're all caught up")}</p>
      <p className="text-sm text-muted-foreground">{t('Anything waiting on you will show up here.')}</p>
    </div>
  );

  return (
    <div>
      {visibleItems.length === 0
        ? emptyState
        : grouped.map(({ group, items: groupItems }) => (
            <section key={group} className="mb-4 last:mb-0" aria-label={group === 'ForYou' ? t('For you') : t('Approvals')}>
              <div className="mb-1.5 flex items-center gap-2 px-4">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {group === 'ForYou' ? t('For you') : t('Approvals')}
                </h3>
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
                  {countsByGroup[group] ?? groupItems.length}
                </span>
              </div>
              <div className="divide-y divide-border">
                {groupItems.map((item) => (
                  <PendingActionRow
                    key={rowKey(item)}
                    item={item}
                    locale={locale}
                    busy={surveyBusy?.key === rowKey(item) ? surveyBusy.decision : busyFor(item)}
                    onDecision={requestDecision}
                    onOpen={handleDeepDive}
                    onDetails={handleDeepDive}
                    onQuickRate={runQuickRate}
                    detailsNavigates={detailsNavigates(item)}
                  />
                ))}
              </div>
            </section>
          ))}

      {variant === 'dashboard' && totalCount > visibleItems.length && (
        <div className="border-t border-border px-4 pt-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pending-actions')} data-testid="pa-view-all">
            {t('View all ({{n}})', { n: totalCount })}
            <ArrowRight size={14} className="ms-1.5 rtl:-scale-x-100" />
          </Button>
        </div>
      )}

      <DecisionDialog
        target={decisionTarget}
        submitting={submitting}
        onClose={closeDecision}
        onConfirm={confirmDecision}
      />
      <DetailDialog item={detailItem} locale={locale} onClose={() => setDetailItem(null)} />
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default PendingActionsPanel;
